'use client';
import { INotificationResponse } from '@notification/service/typings';
import { IConversationSet } from '@ui/components';
import { useCallback, useMemo } from 'react';

interface UseCommunityTabsProps {
  creatorDisplayName?: string;
  conversationType?: string;
  hasCreatorEnabledTopics?: boolean;
  hasCreatorEnabledFansCanCreatePosts?: boolean;
  isPostACrewPost?: boolean;
  isTopicSelected?: boolean;
  onTabChange?: (tab: string) => void;
  initialSelectedTab?: number;
  customSecondTabLabel?: string;
  defaultCommunitySet?: IConversationSet | null;
  newPostNotifications: INotificationResponse | undefined;
}

interface UseCommunityTabsReturn {
  tabs: string[];
  selectedTab: number;
  resourceTabs: Array<{
    label: string;
    labelTextAlias: string;
    title: string;
    content: React.ReactNode;
    unreadCount: number;
  }>;
  onTabChanged: (tab: string) => void;
}

export const useCommunityTabs = ({
  creatorDisplayName,
  conversationType,
  hasCreatorEnabledTopics,
  hasCreatorEnabledFansCanCreatePosts,
  isPostACrewPost,
  isTopicSelected,
  onTabChange,
  initialSelectedTab,
  customSecondTabLabel,
  defaultCommunitySet,
  newPostNotifications,
}: UseCommunityTabsProps): UseCommunityTabsReturn => {
  const tabs = useMemo(() => {
    if (!creatorDisplayName) return [];

    // If defaultCommunitySet is null/undefined, return empty to prevent stale data
    if (!defaultCommunitySet) return [];

    // Use custom labels from community_miscellaneous if available, otherwise fallback to defaults
    const post_creator_segment_label =
      defaultCommunitySet?.conversation?.miscellaneous?.post_creator_segment_label ||
      creatorDisplayName;
    const post_community_segment =
      defaultCommunitySet?.conversation?.miscellaneous?.post_community_segment || 'crew';
    const topic_community_segment =
      defaultCommunitySet?.conversation?.miscellaneous?.topics_tab_label || 'Topic';

    const tabList = [post_creator_segment_label];

    if (hasCreatorEnabledTopics) {
      tabList.push(topic_community_segment);
    } else if (hasCreatorEnabledFansCanCreatePosts) {
      tabList.push(post_community_segment);
    } else if (customSecondTabLabel) {
      tabList.push(customSecondTabLabel);
    } else {
      tabList;
    }
    return tabList;
  }, [
    creatorDisplayName,
    hasCreatorEnabledTopics,
    customSecondTabLabel,
    defaultCommunitySet,
    hasCreatorEnabledFansCanCreatePosts,
  ]);

  const selectedTab = useMemo(() => {
    if (tabs.length === 0) {
      return 0;
    }

    if (initialSelectedTab !== undefined) {
      // Ensure the initialSelectedTab is within bounds of the tabs array
      return Math.min(Math.max(initialSelectedTab, 0), tabs.length - 1);
    }

    if (
      isTopicSelected ||
      conversationType === 'topic' ||
      (isPostACrewPost && !hasCreatorEnabledTopics)
    ) {
      return 1;
    }
    return 0;
  }, [
    tabs.length,
    conversationType,
    initialSelectedTab,
    isPostACrewPost,
    hasCreatorEnabledTopics,
    isTopicSelected,
  ]);

  const onTabChanged = useCallback(
    (tab: string) => {
      const tabIndex = tabs.indexOf(tab);
      if (tabIndex === -1) return;

      onTabChange?.(tab);
    },
    [tabs, onTabChange],
  );

  const resourceTabs = useMemo(() => {
    if (!creatorDisplayName || tabs.length === 0) {
      return [];
    }

    return tabs.map((tab, index) => ({
      label: tab,
      labelTextAlias: tab,
      title:
        index === 0 ? 'Posts' : hasCreatorEnabledTopics && !isTopicSelected ? 'Topics' : 'Posts',
      content: null,
      unreadCount:
        index === 1
          ? (hasCreatorEnabledTopics
              ? newPostNotifications?.unread_global_topics
              : newPostNotifications?.unread_crew_posts) || 0
          : 0,
    }));
  }, [creatorDisplayName, tabs, hasCreatorEnabledTopics, isTopicSelected, newPostNotifications]);

  return {
    tabs,
    selectedTab,
    resourceTabs,
    onTabChanged,
  };
};
